import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة قانون الغاز المثالي - حساب الضغط، الحجم، الحرارة، وعدد المولات";
const DESCRIPTION =
  "حاسبة قانون الغاز المثالي المجانية - احسب الضغط (P)، الحجم (V)، درجة الحرارة (T)، أو عدد المولات (n) بدقة. أداة سهلة للفيزياء والكيمياء مع دعم لمختلف الوحدات العلمية.";
const KEYWORDS =
  "حاسبة قانون الغاز المثالي, حساب الضغط, حساب الحجم, درجة حرارة الغاز, عدد المولات, ثابت الغازات العام, ideal gas law calculator, PV=nRT";
const OG_URL = "https://do-calculate.com/calculator/ar/ideal-gas-law/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة قانون الغاز المثالي",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة قانون الغاز المثالي",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو قانون الغاز المثالي؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "قانون الغاز المثالي هو معادلة الحالة لغاز مثالي افتراضي، ويتم التعبير عنه بالصيغة PV = nRT، حيث P هو الضغط، V هو الحجم، n هو كمية المادة (بالمولات)، R هو ثابت الغاز المثالي، و T هي درجة الحرارة المطلقة.",
        },
      },
      {
        "@type": "Question",
        name: "ما هي قيمة ثابت الغاز العام (R)؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "تعتمد قيمة R على الوحدات المستخدمة. القيمة الأكثر شيوعاً هي 0.08206 (L⋅atm)/(mol⋅K) عند استخدام الغلاف الجوي واللتر، أو 8.314 J/(mol⋅K) عند استخدام الوحدات الدولية (باسكال ومتر مكعب).",
        },
      },
    ],
  },
];

const GAS_CONSTANT_R = 0.082061; // L⋅atm / (mol⋅K)

function IdealGasLawCalculator() {
  const [solveFor, setSolveFor] = useState("pressure");
  const [pressure, setPressure] = useState("");
  const [volume, setVolume] = useState("");
  const [moles, setMoles] = useState("");
  const [temperature, setTemperature] = useState("");

  const [pUnit, setPUnit] = useState("atm");
  const [vUnit, setVUnit] = useState("L");
  const [tUnit, setTUnit] = useState("K");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const pFactors = { atm: 1, kPa: 101.325, Pa: 101325, bar: 1.01325, mmHg: 760 };
  const vFactors = { L: 1, mL: 1000, m3: 0.001, cm3: 1000 };

  const toKelvin = (val, unit) => {
    const v = parseFloat(val);
    if (unit === "C") return v + 273.15;
    if (unit === "F") return (v - 32) * (5 / 9) + 273.15;
    return v;
  };

  const fromKelvin = (kelvin, unit) => {
    if (unit === "C") return kelvin - 273.15;
    if (unit === "F") return (kelvin - 273.15) * 1.8 + 32;
    return kelvin;
  };

  const calculate = () => {
    setError("");
    try {
      const P = parseFloat(pressure) / pFactors[pUnit];
      const V = parseFloat(volume) / vFactors[vUnit];
      const n = parseFloat(moles);
      const T = toKelvin(temperature, tUnit);

      if (solveFor !== "pressure" && P <= 0) throw new Error("يجب أن يكون الضغط أكبر من الصفر");
      if (solveFor !== "volume" && V <= 0) throw new Error("يجب أن يكون الحجم أكبر من الصفر");
      if (solveFor !== "moles" && n <= 0) throw new Error("يجب أن يكون عدد المولات أكبر من الصفر");
      if (solveFor !== "temperature" && T <= 0) throw new Error("درجة الحرارة المطلقة يجب أن تكون فوق الصفر المطلق");

      let calcResult;
      let label = "";
      let unitLabel = "";

      switch (solveFor) {
        case "pressure":
          calcResult = (n * GAS_CONSTANT_R * T) / V;
          setResult({
            val: calcResult * pFactors[pUnit],
            label: "الضغط (P)",
            unit: pUnit
          });
          break;
        case "volume":
          calcResult = (n * GAS_CONSTANT_R * T) / P;
          setResult({
            val: calcResult * vFactors[vUnit],
            label: "الحجم (V)",
            unit: vUnit
          });
          break;
        case "moles":
          calcResult = (P * V) / (GAS_CONSTANT_R * T);
          setResult({
            val: calcResult,
            label: "عدد المولات (n)",
            unit: "mol"
          });
          break;
        case "temperature":
          calcResult = (P * V) / (n * GAS_CONSTANT_R);
          setResult({
            val: fromKelvin(calcResult, tUnit),
            label: "درجة الحرارة (T)",
            unit: tUnit === "K" ? "كلفن" : tUnit === "C" ? "مئوية" : "فهرنهايت"
          });
          break;
        default:
          break;
      }
    } catch (err) {
      setResult(null);
      if (pressure || volume || moles || temperature) {
         setError(err.message || "يرجى التأكد من إدخال قيم صحيحة");
      }
    }
  };

  useEffect(() => {
    const inputs = { pressure, volume, moles, temperature };
    const required = Object.keys(inputs).filter(k => k !== solveFor);
    if (required.every(k => inputs[k] !== "" && !isNaN(inputs[k]))) {
      calculate();
    } else {
      setResult(null);
    }
  }, [solveFor, pressure, volume, moles, temperature, pUnit, vUnit, tUnit]);

  const handleClear = () => {
    setPressure("");
    setVolume("");
    setMoles("");
    setTemperature("");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />

      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة قانون الغاز المثالي</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                استخدم <strong>حاسبة قانون الغاز المثالي</strong> لإيجاد أي من المتغيرات الأربعة في معادلة الحالة (PV = nRT). سواء كنت بحاجة لحساب الضغط، الحجم، كمية المادة بالمولات، أو درجة الحرارة، توفر هذه الأداة نتائج دقيقة مع تحويل آلي للوحدات.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>أدخل القيم المتاحة للحساب</h2>
              </div>

              <div className="row mb-3">
                <div className="col-12">
                  <label >المتغير المراد حسابه</label>
                  <select className="form-select" value={solveFor} onChange={(e) => { setSolveFor(e.target.value); setResult(null); }}>
                    <option value="pressure">الضغط (P)</option>
                    <option value="volume">الحجم (V)</option>
                    <option value="moles">عدد المولات (n)</option>
                    <option value="temperature">درجة الحرارة (T)</option>
                  </select>
                </div>
              </div>

              <div className="row">
                {solveFor !== "pressure" && (
                  <div className="col-md-6 mb-3">
                    <label >الضغط</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={pressure} onChange={(e) => setPressure(e.target.value)} placeholder="0.00" />
                      <select className="form-select" style={{ maxWidth: "100px" }} value={pUnit} onChange={(e) => setPUnit(e.target.value)}>
                        <option value="atm">atm</option>
                        <option value="kPa">kPa</option>
                        <option value="Pa">Pa</option>
                        <option value="bar">bar</option>
                        <option value="mmHg">mmHg</option>
                      </select>
                    </div>
                  </div>
                )}

                {solveFor !== "volume" && (
                  <div className="col-md-6 mb-3">
                    <label >الحجم</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={volume} onChange={(e) => setVolume(e.target.value)} placeholder="0.00" />
                      <select className="form-select" style={{ maxWidth: "100px" }} value={vUnit} onChange={(e) => setVUnit(e.target.value)}>
                        <option value="L">لتر</option>
                        <option value="mL">مل</option>
                        <option value="m3">m³</option>
                      </select>
                    </div>
                  </div>
                )}

                {solveFor !== "moles" && (
                  <div className="col-md-6 mb-3">
                    <label >عدد المولات (n)</label>
                    <input type="number" className="form-control" value={moles} onChange={(e) => setMoles(e.target.value)} placeholder="mol" />
                  </div>
                )}

                {solveFor !== "temperature" && (
                  <div className="col-md-6 mb-3">
                    <label >درجة الحرارة</label>
                    <div className="input-group">
                      <input type="number" className="form-control" value={temperature} onChange={(e) => setTemperature(e.target.value)} placeholder="0.00" />
                      <select className="form-select" style={{ maxWidth: "100px" }} value={tUnit} onChange={(e) => setTUnit(e.target.value)}>
                        <option value="K">K</option>
                        <option value="C">°C</option>
                        <option value="F">°F</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center mt-3">
                <button onClick={handleClear} className="btn btn-secondary px-5"style={{marginRight:'3px'}}>امسح</button>
              </div>

              {error && <div className="alert alert-danger mt-3">{error}</div>}

              {result && (
                <div className="mt-4">
                  <h3 className="text-center mb-3">النتيجة:</h3>
                  <table className="table bmi-table text-center">
                    <thead>
                      <tr>
                        <th >البيان</th>
                        <th >القيمة المحسوبة</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{result.label}</td>
                        <td style={{ fontSize: "20px", fontWeight: "bold", color: "#1a5f3f" }}>
                          {result.val.toFixed(4)} {result.unit}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <hr />
            <RelatedLinks />
          </div>
        </div>

        <div className="col-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>ما هو قانون الغاز المثالي؟</strong></div>
              <div className="info-section-body">
                <p>قانون الغاز المثالي هو معادلة تربط بين أربعة متغيرات أساسية للغاز: الضغط، الحجم، درجة الحرارة، وكمية المادة. يُفترض في هذا القانون أن جزيئات الغاز لا تشغل حيزاً ولا توجد بينها قوى تجاذب (غاز مثالي).</p>
                <p><strong>المعادلة:</strong> <code>PV = nRT</code></p>
                <ul>
                  <li><strong>P:</strong> الضغط (Pressure)</li>
                  <li><strong>V:</strong> الحجم (Volume)</li>
                  <li><strong>n:</strong> عدد المولات (Number of moles)</li>
                  <li><strong>R:</strong> ثابت الغاز المثالي (0.0821 L·atm/mol·K)</li>
                  <li><strong>T:</strong> درجة الحرارة المطلقة بالكلفن (Temperature in Kelvin)</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>أمثلة عملية</strong></div>
              <div className="info-section-body">
                <p><strong>مثال 1:</strong> حساب الضغط لـ 2 مول من غاز في حجم 10 لتر عند درجة حرارة 300 كلفن.</p>
                <ul>
                  <li>n = 2, V = 10, T = 300</li>
                  <li>P = (2 × 0.0821 × 300) / 10 = 4.926 atm</li>
                </ul>
                <p><strong>مثال 2:</strong> ما الحجم الذي يشغله 1 مول من الغاز في الظروف المعيارية (1 atm و 273.15 K)؟</p>
                <ul>
                  <li>V = (1 × 0.0821 × 273.15) / 1 = 22.4 لتر تقريباً.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>الأسئلة الشائعة (FAQ)</strong></div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. لماذا يجب استخدام الكلفن دائماً؟</strong>
                  <p>قانون الغازات يعتمد على مقياس حرارة يبدأ من الصفر المطلق. استخدام الدرجة المئوية سيؤدي لنتائج خاطئة لأن الصفر المئوي لا يعني غياب الطاقة الحرارية.</p>
                </div>
                <div className="mb-3">
                  <strong>2. متى يفشل قانون الغاز المثالي؟</strong>
                  <p>يفشل القانون عند الضغوط العالية جداً أو درجات الحرارة المنخفضة جداً، حيث تقترب الجزيئات من بعضها وتصبح قوى التجاذب وحجم الجزيئات مؤثراً.</p>
                </div>
                <div className="mb-3">
                  <strong>3. ما الفرق بين الغاز المثالي والغاز الحقيقي؟</strong>
                  <p>الغاز الحقيقي له جزيئات تشغل حيزاً وتتجاذب، بينما الغاز المثالي هو نموذج رياضي مبسط يسهل الحسابات في الظروف العادية.</p>
                </div>
                <div className="mb-3">
                  <strong>4. كيف أحسب n إذا كان لدي الكتلة؟</strong>
                  <p>يمكنك حساب عدد المولات (n) بقسمة كتلة الغاز (بالجرام) على كتلته المولية (g/mol) من الجدول الدوري.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IdealGasLawCalculator;